import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, Plus, ChevronRight, Users as UsersIcon } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { useAuth } from "@/contexts/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { isDemoProject, TrainingBadge } from "@/lib/demoProjects";

const STATUS_OPTIONS = ["Alle","Erstkontakt","Termin geplant","Vor-Ort-Aufnahme","Technische Prüfung","Angebot erstellt","Angebot versendet","Nachfassen","Gewonnen","Verloren","Archiviert"];

const STATUS_COLORS = {
  "Erstkontakt": "bg-zinc-700",
  "Termin geplant": "bg-blue-600",
  "Vor-Ort-Aufnahme": "bg-indigo-600",
  "Technische Prüfung": "bg-violet-600",
  "Angebot erstellt": "bg-amber-600",
  "Angebot versendet": "bg-orange-600",
  "Nachfassen": "bg-pink-600",
  "Gewonnen": "bg-[#00E575] text-[#09090B]",
  "Verloren": "bg-red-700",
  "Archiviert": "bg-zinc-600",
};

function PersonSelect({ value, onChange, managers, reps, testid, className }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={className}>
      <option value="all">Alle Personen</option>
      {managers.length > 0 && (
        <optgroup label="Vertriebsleiter (inkl. Teams)">
          {managers.map((m) => (
            <option key={m.id} value={`manager:${m.id}`}>
              {m.first_name} {m.last_name} {m.role !== "sales_manager" ? `· ${ROLE_LABEL[m.role]}` : ""}
            </option>
          ))}
        </optgroup>
      )}
      {reps.length > 0 && (
        <optgroup label="Vertrieb">
          {reps.map((r) => (
            <option key={r.id} value={`owner:${r.id}`}>{r.first_name} {r.last_name}</option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

const ROLE_LABEL = {
  admin: "Admin",
  sales_director: "Direktor",
  sales_manager: "Leiter",
  sales_rep: "Vertrieb",
};

export default function ProjectList() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Alle");
  const [personFilter, setPersonFilter] = useState("all"); // "all" | "manager:<id>" | "owner:<id>"
  const [team, setTeam] = useState([]); // visible users for the person picker
  const [loading, setLoading] = useState(true);

  const canFilterByPerson = user && ["admin", "sales_director", "sales_manager"].includes(user.role);
  const isInstaller = user?.role === "installer";

  // Load the people the current user is allowed to filter by.
  useEffect(() => {
    if (!canFilterByPerson) return;
    api.get("/users").then((r) => setTeam(Array.isArray(r.data) ? r.data : [])).catch(() => setTeam([]));
  }, [canFilterByPerson]);

  const managers = useMemo(
    () => team.filter((u) => u.role === "sales_manager" || u.role === "sales_director"),
    [team],
  );
  const reps = useMemo(() => team.filter((u) => u.role === "sales_rep"), [team]);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    const params = {};
    if (status !== "Alle") params.status = status;
    if (search) params.search = search;
    if (personFilter.startsWith("manager:")) params.manager_id = personFilter.slice(8);
    if (personFilter.startsWith("owner:")) params.owner_user_id = personFilter.slice(6);
    api.get("/projects", { params }).then((r) => setProjects(r.data)).finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, personFilter]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Projekte" />

      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 ios-no-zoom">
        {/* Title row with the "Neu" button aligned to the section, not the header */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="font-display text-lg font-semibold text-zinc-100">Übersicht <span className="text-[13px] font-mono text-zinc-500 font-normal" data-testid="projectlist-count">{loading ? "" : projects.length}</span></h2>
          {!isInstaller && (
            <Link
              to="/projects/new"
              data-testid="projectlist-new-btn"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-[#00E575] text-[#09090B] font-semibold hover:bg-[#00C964] transition active:scale-95"
              aria-label="Neues Projekt"
            >
              <Plus size={18} /> <span>Neu</span>
            </Link>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-2 sm:gap-3 mb-2 sm:mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <input
              data-testid="projectlist-search-input"
              type="text"
              placeholder="Suche: Kunde, Ort, PLZ, Nummer, Angebot…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="h-11 sm:h-12 w-full bg-[#18181B] border border-zinc-800 rounded-xl pl-12 pr-4 text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none"
            />
          </div>
          <div className={`grid gap-2 md:contents ${canFilterByPerson && (managers.length > 0 || reps.length > 0) ? "grid-cols-2" : "grid-cols-1"}`}>
          <div className="relative min-w-0">
            <Filter className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" size={16} />
            <select
              data-testid="projectlist-status-filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="h-11 sm:h-12 bg-[#18181B] border border-zinc-800 rounded-xl pl-9 sm:pl-12 pr-6 sm:pr-10 text-zinc-50 focus:border-[#00E575] outline-none appearance-none md:min-w-[180px] w-full md:w-auto truncate"
            >
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

        {canFilterByPerson && (managers.length > 0 || reps.length > 0) && (
          <div className="relative min-w-0 md:hidden">
            <UsersIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" size={16} />
            <PersonSelect value={personFilter} onChange={setPersonFilter} managers={managers} reps={reps}
              testid="projectlist-person-filter-mobile" className="h-11 w-full bg-[#18181B] border border-zinc-800 rounded-xl pl-9 pr-6 text-zinc-50 focus:border-[#00E575] outline-none appearance-none truncate" />
          </div>
        )}
          </div>
        </div>

        {canFilterByPerson && (managers.length > 0 || reps.length > 0) && (
          <div className="relative mb-6 hidden md:block">
            <UsersIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" size={18} />
            <PersonSelect value={personFilter} onChange={setPersonFilter} managers={managers} reps={reps}
              testid="projectlist-person-filter" className="h-12 w-full bg-[#18181B] border border-zinc-800 rounded-xl pl-12 pr-10 text-zinc-50 focus:border-[#00E575] outline-none appearance-none" />
          </div>
        )}
        <div className="md:hidden h-2" />

        {loading ? (
          <div className="text-zinc-300 text-center font-medium py-12">Lade Projekte…</div>
        ) : projects.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-12 text-center">
            <p className="text-zinc-400 mb-4">Keine Projekte gefunden.</p>
            {!isInstaller && (
              <Link to="/projects/new" className="inline-flex items-center gap-2 bg-[#00E575] text-[#09090B] font-semibold px-5 py-2.5 rounded-xl hover:bg-[#00C964] transition">
                <Plus size={18} /> Erstes Projekt
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                data-testid={`projectlist-item-${p.id}`}
                className="block bg-[#18181B] border border-zinc-800 rounded-2xl p-3 sm:p-4 hover:border-[#00E575]/50 transition active:scale-[0.99]"
              >
                <div className="flex items-start sm:items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate flex items-center gap-2 flex-wrap text-[15px] sm:text-base">
                      <span className="truncate max-w-full">{p.customer_name || "—"}</span>
                      {isDemoProject(p) && <TrainingBadge />}
                      {p.is_real_workflow && (
                        <span
                          data-testid={`project-real-badge-${p.id}`}
                          title="Echter Vorgang — vor Test-Reset geschützt"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 uppercase tracking-wider"
                        >
                          ✓ Echt
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-400 font-mono mt-0.5 truncate">{p.project_number} · {p.customer_zip} {p.customer_city}</div>
                    <div className="flex items-center gap-2 mt-1.5 sm:mt-0.5 flex-wrap">
                      <span className={`sm:hidden px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider max-w-full truncate ${STATUS_COLORS[p.status] || "bg-zinc-700"}`} data-testid={`projectlist-status-mobile-${p.id}`}>{p.status}</span>
                      {p.offer_number && <span className="text-xs text-[#00E575] font-mono">Angebot: {p.offer_number}</span>}
                    </div>
                  </div>
                  <span className={`hidden sm:inline-block px-2.5 py-1 rounded-md text-[12px] font-medium uppercase tracking-wider whitespace-nowrap ${STATUS_COLORS[p.status] || "bg-zinc-700"}`}>{p.status}</span>
                  <ChevronRight size={18} className="sm:hidden text-zinc-600 shrink-0 mt-1" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
